import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@rsuite/icons';
import { Pagination, Dropdown } from 'rsuite';
import {
  FaRegTimesCircle,
  FaRegCheckCircle,
  FaRegHourglass,
  FaRegFileExcel,
  FaArrowAltCircleDown,
  FaArrowAltCircleUp,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import ls from 'localstorage-slim';
import { useTranslation } from 'react-i18next';

import Loading from '../GeneralComponents/Loading';
import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';
import DataContext from '../context/InvoiceData';

function AllInvoices() {
  let navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { setStep, setDocumentType, setDocumentTypeVersion } = useContext(DataContext);

  const [invoices, setInvoices] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [activePage, setActivePage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTaxpayer, setCurrentTaxpayer] = useState('');

  const subDomain = ls.get('subDomain');

  const handleCopyInvoice = (uuid, type, version) => {
    axios
      .get(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/copy_invoice`, {
        params: {
          uuid: uuid,
        },
      })
      .then(response => {
        setStep(0);
        setDocumentType(type === 'i' ? 'Invoice' : type === 'c' ? 'Credit Note' : 'Debit Note');
        setDocumentTypeVersion(version);
        ls.set('id', response.data);
        navigate('/submitInvoiceSteps');
      })
      .catch(error => {
        console.log(error);
      });
  };

  useEffect(() => {
    async function fetchData() {
      const documentToast = toast.loading('Please wait...');
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/recent_documents`,
          {
            params: {
              ETAToken: ls.get('ETAToken'),
              pageNumber: activePage,
              taxpayer: ls.get('taxpayer_id'),
            },
          }
        );

        setInvoices(response.data.data.result);
        setMetaData(response.data.data.metadata);
        setCurrentTaxpayer(response.data.rin);
        setIsLoading(false);

        toast.update(documentToast, {
          render: 'Documents retrieved successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
          toast.update(documentToast, {
            render: `${error.response.data}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
          toast.update(documentToast, {
            render: `${error.request}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
          toast.update(documentToast, {
            render: `${error.message}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        }
      }
    }

    fetchData();
  }, [activePage]);
  return isLoading ? (
    <div style={{ height: '77vh' }}>
      <Loading />
    </div>
  ) : (
    <>
      <div className='container-fluid'>
        <div className='row justify-content-center mx-2'>
          <table className='table mt-2' dir={`${i18n.language === 'ar' && 'rtl'}`}>
            <thead
              style={{
                verticalAlign: 'middle',
              }}>
              <tr className='text-center'>
                <th className='col'></th>
                <th className='col'>{t('document.table.id')}</th>
                <th className='col'>{t('document.table.dateTime')}</th>
                <th className='col'>{t('document.table.type')}</th>
                <th className='col'>{t('document.table.total')}</th>
                <th className='col'>{t('document.table.issuer')}</th>
                <th className='col'>{t('document.table.receiver')}</th>
                <th className='col'>{t('document.table.submission')}</th>
                <th className='col'>{t('document.table.status')}</th>
                <th className='col'></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(invoice => (
                <tr key={invoice.longId}>
                  <td className='text-center'>
                    {currentTaxpayer === invoice.issuerId ? (
                      <Icon as={FaArrowAltCircleUp} color='black' className='me-1' />
                    ) : (
                      <Icon as={FaArrowAltCircleDown} color='blue' className='me-1' />
                    )}
                  </td>
                  <td className='text-center'>
                    <span
                      className='d-block fw-bold text-truncate'
                      style={{ maxWidth: '180px' }}
                      dir='ltr'>
                      <Link to={`../../documents/${invoice.uuid}`}>{invoice.uuid}</Link>
                    </span>
                    <span className='d-block'>{invoice.internalId}</span>
                  </td>
                  <td className='text-center'>
                    <span className='d-block fw-bold'>
                      {moment(invoice.dateTimeReceived).format('DD/MM/YYYY')}
                    </span>
                    <span className='d-block' dir='ltr'>
                      {moment(invoice.dateTimeReceived).format('h:mm a')}
                    </span>
                  </td>
                  <td className='text-center'>
                    <span className='d-block fw-bold'>
                      {i18n.language === 'en'
                        ? invoice.documentTypeNamePrimaryLang
                        : invoice.documentTypeNameSecondaryLang}
                    </span>
                    <span className='d-block'>{invoice.typeVersionName}</span>
                  </td>
                  <td className='text-center'>
                    <div className='py-2'>{invoice.total.toFixed(2)}</div>
                  </td>
                  <td className='text-center'>
                    <span
                      className='d-block fw-bold text-nowrap text-truncate'
                      style={{ maxWidth: '180px' }}>
                      {invoice.issuerName}
                    </span>
                    <span className='d-block'>{invoice.issuerId}</span>
                  </td>
                  <td className='text-center'>
                    <span
                      className='d-block fw-bold text-nowrap text-truncate'
                      style={{ maxWidth: '180px' }}>
                      {invoice.receiverName}
                    </span>
                    <span className='d-block'>{invoice.receiverId}</span>
                  </td>
                  <td className='text-truncate text-center' style={{ maxWidth: '180px' }} dir='ltr'>
                    <div className='py-2'>{invoice.submissionUUID}</div>
                  </td>
                  <td className='text-center'>
                    <span className='d-block'>
                      {invoice.status === 'Invalid' || invoice.status === 'Rejected' ? (
                        <Icon as={FaRegTimesCircle} color='red' />
                      ) : invoice.status === 'Valid' &&
                        !invoice.cancelRequestDate &&
                        !invoice.rejectRequestDate ? (
                        <Icon as={FaRegCheckCircle} color='green' />
                      ) : (invoice.cancelRequestDate &&
                          invoice.cancelRequestDelayedDate > new Date().toISOString()) ||
                        (invoice.rejectRequestDate &&
                          invoice.rejectRequestDelayedDate > new Date().toISOString()) ? (
                        <Icon as={FaRegHourglass} color='grey' />
                      ) : (
                        <Icon as={FaRegFileExcel} color='grey' />
                      )}
                    </span>
                    <span
                      className={`${
                        ['Invalid', 'Rejected'].includes(invoice.status)
                          ? 'text-danger'
                          : invoice.status === 'Valid'
                          ? 'text-success'
                          : 'text-muted'
                      }`}>
                      {invoice.cancelRequestDate &&
                      invoice.cancelRequestDelayedDate > new Date().toISOString()
                        ? `${invoice.status} \u2192 Cancelled`
                        : invoice.rejectRequestDate &&
                          invoice.rejectRequestDelayedDate > new Date().toISOString()
                        ? `${invoice.status} \u2192 Rejected`
                        : invoice.status}
                    </span>
                  </td>
                  <td className={`${invoice.isSobolIssued ? 'text-end' : 'd-none'}`} role='button'>
                    <Dropdown
                      placement={`${i18n.language === 'en' ? 'bottomEnd' : 'bottomStart'}`}
                      noCaret
                      title='&#x22EE;'>
                      <Dropdown.Item
                        onClick={() => {
                          handleCopyInvoice(
                            invoice.uuid,
                            invoice.typeName,
                            invoice.typeVersionName
                          );
                        }}>
                        Copy Invoice
                      </Dropdown.Item>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invoices.length === 0 && (
            <div className='d-flex justify-content-center'>
              <span>{t('document.table.noRecords')}</span>
            </div>
          )}
          <div className='row'>
            <Pagination
              prev
              next
              total={metaData['totalCount']}
              limit={10}
              activePage={activePage}
              onChangePage={setActivePage}
              className='my-2 justify-content-center'
              size='md'
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default AllInvoices;
